package fr.frux.feature.map.aim

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.unit.DpOffset
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapCameraPosition
import fr.frux.core.maps.MapCameraState
import fr.frux.core.maps.locationAtOffset
import fr.frux.shared.domain.model.DuplicateCandidate
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.findDuplicateCandidates

/** Zoom en dessous duquel viser un arbre n'a plus de sens : la carte zoome au demarrage. */
const val AIM_MIN_ZOOM = 15f

/** Zoom d'approche quand la carte est trop large pour viser. */
const val AIM_PICK_ZOOM = 17f

/** Repos de la camera avant de chercher un doublon : aucun calcul par frame de geste. */
const val AIM_DUPLICATE_DEBOUNCE_MS = 250L

/**
 * Cercle de borne du deplacement : la pointe de l'epingle doit rester a moins de [radiusMeters] de
 * [center]. [scientificName] pilote le glyphe de la position actuelle.
 */
data class AimCircle(
    val center: Location,
    val radiusMeters: Double,
    val scientificName: String? = null,
)

/**
 * Etat du mode visee de la carte persistante (ADR 0009) : une epingle fixe, on deplace la carte.
 * Vit dans [fr.frux.feature.map.MapHostState] et n'emprunte pas le chemin `onCameraMoved` du
 * ViewModel : aucune requete reseau par mouvement, le candidat se calcule sur les arbres deja
 * charges.
 */
@Stable
class MapAimState internal constructor(
    private val camera: MapCameraState,
) {
    var isActive: Boolean by mutableStateOf(false)
        private set

    var candidate: DuplicateCandidate? by mutableStateOf(null)
        internal set

    var circle: AimCircle? by mutableStateOf(null)
        private set

    /** Arbre en cours de deplacement : jamais son propre doublon, et dessine par le cercle. */
    var excludedTreeId: Long? by mutableStateOf(null)
        private set

    /** Distance de la pointe de l'epingle au centre du cercle, `null` sans cercle. Suit la camera. */
    val targetDistanceMeters: Double?
        get() = circle?.let { locationAtOffset(camera.position, pinOffset).distanceMetersTo(it.center) }

    val isOutsideCircle: Boolean
        get() {
            val distance = targetDistanceMeters ?: return false
            return distance > (circle?.radiusMeters ?: return false)
        }

    /**
     * Position figee par l'etape 2 ou 3 de l'assistant, hors visee : en grand ecran la carte reste
     * visible a cote, l'epingle y marque ou l'arbre sera cree (#629). `null` sinon.
     */
    var frozenPosition: Location? by mutableStateOf(null)
        private set

    fun showFrozen(location: Location?) {
        frozenPosition = location
    }

    /** Decalage de l'epingle par rapport au centre de la vue, ecrit par la couche de mise en page. */
    internal var pinOffset: DpOffset by mutableStateOf(DpOffset.Zero)

    /**
     * Entre en mode visee. Avec [center], la pointe de l'epingle tombe sur ce point. Sans, la
     * camera reste ou elle est, zoomee a [AIM_PICK_ZOOM] seulement si elle est sous [AIM_MIN_ZOOM].
     */
    fun start(
        center: Location?,
        circle: AimCircle? = null,
        excludedTreeId: Long? = null,
    ) {
        isActive = true
        this.excludedTreeId = excludedTreeId
        candidate = null
        this.circle = circle
        locateRequested = false
        val current = camera.position
        if (center != null) {
            moveTipTo(center, current.zoom.coerceAtLeast(AIM_MIN_ZOOM))
        } else if (current.zoom < AIM_MIN_ZOOM) {
            camera.moveTo(MapCameraPosition(current.target, AIM_PICK_ZOOM))
        }
    }

    /**
     * Pose la pointe de l'epingle sur [location], zoomee au moins a [AIM_PICK_ZOOM]. Remplace le
     * cadrage d'Explorer, qui recentrerait la camera et non la pointe, et pourrait dezoomer.
     */
    fun centerOn(location: Location) {
        moveTipTo(location, camera.position.zoom.coerceAtLeast(AIM_PICK_ZOOM))
    }

    /** Ramene la pointe au centre du cercle de borne, sans effet hors deplacement. */
    fun recenterOnCircle() {
        circle?.let { centerOn(it.center) }
    }

    /** Un « me localiser » attend une position : [MapSurface] centre la visee des qu'elle arrive. */
    internal var locateRequested: Boolean by mutableStateOf(false)
        private set

    /**
     * Valeur de `MapState.locateSequence` au moment de la demande : la mesure attendue est la
     * premiere qui se termine apres. Evite de centrer sur l'ancienne position (#630) sans observer
     * `isLocating`, dont l'aller-retour est conflate par le StateFlow quand le fix est immediat (#672).
     */
    private var locateBaseline: Int = 0

    internal fun requestLocate(locateSequence: Int) {
        locateRequested = true
        locateBaseline = locateSequence
    }

    /**
     * Resout un « me localiser » en cours : rend la position a centrer, seulement une fois une
     * mesure terminee depuis la demande ([locateSequence] au-dela de la valeur relevee). Avant,
     * [location] est la derniere position connue, pas la fraiche. Le drapeau n'est baisse qu'a ce
     * moment.
     */
    internal fun consumeLocate(
        locateSequence: Int,
        location: Location?,
    ): Location? {
        val done = isActive && locateRequested && locateSequence > locateBaseline
        if (done) locateRequested = false
        return location.takeIf { done }
    }

    private fun moveTipTo(
        location: Location,
        zoom: Float,
    ) {
        val offset = DpOffset(-pinOffset.x, -pinOffset.y)
        camera.moveTo(MapCameraPosition(locationAtOffset(MapCameraPosition(location, zoom), offset), zoom))
    }

    fun stop() {
        locateRequested = false
        isActive = false
        candidate = null
        circle = null
        excludedTreeId = null
    }

    /** Point geographique sous la pointe de l'epingle. */
    fun currentTarget(): Location = locationAtOffset(camera.position, pinOffset)

    internal fun onAimSettled(trees: List<Tree>) {
        // Un calcul debounce peut arriver apres stop() : ne pas rendre un candidat a une visee finie.
        if (!isActive) return
        val others = trees.filterNot { it.id == excludedTreeId }
        candidate = findDuplicateCandidates(others, currentTarget(), null) { null }.firstOrNull()
    }
}
